import { Component, OnInit } from '@angular/core';
import { ConfigService } from './config.service';
import { filter } from 'rxjs/operators';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { PlatformService } from './platform.service';
import { ScriptLoaderService } from './scripts-loader-service';
import { ProviderCacheService } from './entry/ProviderCacheService';
import { USE_NEW_FOOTER } from './footer/footer.component';

declare var gtag;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
})
export class AppComponent implements OnInit {
  protected readonly useNewFooter = USE_NEW_FOOTER;

  constructor(
    router: Router,
    private configService: ConfigService,
    private platform: PlatformService,
    private scriptLoader: ScriptLoaderService,
     private providerCache: ProviderCacheService,
  ) {
    const navEndEvent$ = router.events.pipe(
      filter((e) => e instanceof NavigationEnd)
    );
    if (this.platform.isBrowserPlatform()) {
      // Recover from a stale lazy-loaded chunk: the remaining lazy routes (api-docs,
      // download, entry) can fail to load if the server was redeployed with new chunk
      // hashes while this tab had an older build's chunk URLs already loaded — the
      // dynamic import() then 404s with this exact error, the navigation silently aborts,
      // and the user is left on a dead click with no visible change (AFPDBE-1418). A hard
      // reload to the URL they were trying to reach re-fetches index.html and a fresh,
      // valid chunk manifest, then completes the navigation they actually wanted — no
      // manual refresh needed.
      //
      // CHUNK_RELOAD_KEY guards against reloading forever if the reload's own fetch also
      // fails (e.g. the server is genuinely unreachable, not just redeployed) — confirmed
      // this loops without the guard. A sessionStorage flag makes each URL eligible for
      // exactly one automatic retry; it's cleared on the next successful navigation, so a
      // later, unrelated stale-chunk incident on a different route still gets its own retry.
      const CHUNK_RELOAD_KEY = 'chunk_reload_attempted_url';
      router.events.pipe(
        filter((e): e is NavigationError => e instanceof NavigationError)
      ).subscribe((e) => {
        const message = e.error?.message || '';
        const isStaleChunk = /failed to fetch dynamically imported module|loading chunk .* failed|chunkloaderror/i.test(message);
        const alreadyRetried = sessionStorage.getItem(CHUNK_RELOAD_KEY) === e.url;
        if (isStaleChunk && !alreadyRetried) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, e.url);
          window.location.href = e.url;
        }
      });
      navEndEvent$.subscribe(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY));

      const CACHE_VERSION = 'v8';
      const storedVersion = localStorage.getItem('provider_cache_version');

      if (storedVersion !== CACHE_VERSION) {
        this.providerCache.clearAllProviders();
        localStorage.setItem('provider_cache_version', CACHE_VERSION);
      }
      navEndEvent$.subscribe((e: NavigationEnd) => {
        gtag('config', this.configService.getConfig().googleAnalyticsTag, {
          page_path: e.urlAfterRedirects,
        });
      });
    }

    if (this.platform.isBrowserPlatform()) {
      
      const platformdocument = this.platform.getDocument();
      if (platformdocument) {
        const headerScript = platformdocument.createElement('script');
        headerScript.async = true;
        headerScript.src =
          'https://www.googletagmanager.com/gtag/js?id=' +
          this.configService.getConfig().googleAnalyticsTag;
        platformdocument.head.prepend(headerScript);
      } else {
        console.warn('Document is not available in SSR context');
      }
    }
  }
  async ngOnInit(): Promise<void> {
    // reCAptcha api token generation JavaScript bundle
    const recaptchaTokenAPIURL = 'https://www.recaptcha.net/recaptcha/enterprise.js?render=' +
          this.configService.getConfig().recaptchaVerifyKey;
          
    if (this.platform.isBrowserPlatform()) {
      await this.scriptLoader.loadScript('https://cdn.jsdelivr.net/npm/babel-polyfill/dist/polyfill.min.js');
      await this.scriptLoader.loadScript('https://cdn.jsdelivr.net/npm/@webcomponents/webcomponentsjs/webcomponents-lite.js');
      await this.scriptLoader.loadScript('https://cdn.jsdelivr.net/npm/@webcomponents/webcomponentsjs/custom-elements-es5-adapter.js');
      await this.scriptLoader.loadScript('https://www.ebi.ac.uk/pdbe/pdb-component-library/js/protvista-pdb-3.3.0.js');
      await this.scriptLoader.loadScript(recaptchaTokenAPIURL);
    }
  }
}
